import Details from '@/components/Details/Details';
import IBookingRules from '@/types/IBookingRules';

import Tip from '@/components/Tip/Tip';
import TCalendar from '@/components/month-calendar/month-calendar.view';
import calendarService from '@/services/calendar/calendar.service';
import { Box, ButtonGroup, Flex, Heading, IconButton, Input, Text } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { Temporal } from 'temporal-polyfill';
import { useSectionPTO } from './section-pto.controller';

export interface IProps {
	bookingRules: IBookingRules;
	onChange: (values: Partial<IBookingRules>) => void;
}

export default function SectionPto(props: IProps) {
	const { auth, t, state, isLoading, isMutating, timeZone, dispatch, addEventToCalendar, removeEventFromCalendar } =
		useSectionPTO(props);

	return (
		<Details title={t('Busy intervals')}>
			<Tip>{t("Select times on the calendar when you don't want to be available.")}</Tip>

			{/* Menu bar */}
			<Flex justifyContent="space-between" alignItems="center" py={4} px={1} w="100%" overflow="hidden">
				<Heading size="md" textTransform="capitalize">
					{calendarService.formatDate({
						date: DateTime.fromObject({ year: state.from.year, month: state.from.month }).toJSDate(),
						locale: auth.getCurrentUser().locale,
						options: {
							timeZone: timeZone,
							month: 'long',
						},
					})}
				</Heading>

				<Flex justifyContent="space-between" alignItems="center">
					<ButtonGroup isAttached spacing={0}>
						<IconButton
							aria-label={t('Previous')}
							icon={<BsChevronLeft />}
							size="sm"
							onClick={() => {
								dispatch({ from: state.from.minus({ months: 1 }) });
							}}
						></IconButton>
						<Input
							cursor="pointer"
							size="sm"
							variant="filled"
							id="input-from"
							type="date"
							value={state.from.toISODate()!}
							onChange={(e) => {
								dispatch({ from: DateTime.fromISO(e.target.value) as DateTime<true> });
							}}
						/>

						<IconButton
							aria-label={t('Next')}
							icon={<BsChevronRight />}
							size="sm"
							onClick={() => {
								dispatch({ from: state.from.plus({ months: 1 }) });
							}}
						></IconButton>
					</ButtonGroup>
				</Flex>
			</Flex>

			<Box width="100%" h={96} borderRadius="sm" p={0} borderColor="gray.100" borderWidth={1}>
				<TCalendar
					isLoading={isLoading || isMutating}
					timeZone={timeZone}
					onRangeSelected={addEventToCalendar}
					onEventClick={removeEventFromCalendar}
					locale={auth.getCurrentUser().locale}
					year={state.from.year}
					month={state.from.month}
					events={state.busyIntervals.map((e) => ({
						start: Temporal.Instant.from(e.start).toZonedDateTimeISO(timeZone),
						end: Temporal.Instant.from(e.end).toZonedDateTimeISO(timeZone),
						title: t('Busy'),
						color: 'red',
						id: e.start + e.end,
					}))}
				/>
			</Box>
			<Text mt={4} fontSize="small" align="center">
				{timeZone}
			</Text>
		</Details>
	);
}
